import { REPORT_URL_REGEX_PATTERNS } from 'common/Constant';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isMatch } from 'utils/common';

import axios from 'axios';
import { fetchData } from 'features/user/userActions';
import queryString from 'query-string';

import { AppConfig } from 'AppConfig';
import Auth from 'auth/Auth';
import { LOGIN_SUCCESS } from 'features/user/userTypes';
import { setAxiosHeaderToken } from 'networking/NetworkManger';
import { useDispatch } from 'react-redux';
import routePaths from 'routes/routePaths';
import LocalStore from 'utils/LocalStore';
import { JWT } from '../user/userTypes';
import { trackClick, trackingActions, trackingTypes } from 'utils/tracking';

const SSO = () => {
  /**
   * Generates a random JSession ID string in case fo-initial does not return one.
   * The generated string consists of 32 hexadecimal characters in uppercase.
   *
   * @returns {string} A randomly generated JSession ID.
   */
  const generateJsessionId = () => {
    return 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'.replace(/[x]/g, () => ((Math.random() * 16) | 0).toString(16).toUpperCase());
  };

  const navigate = useNavigate();
  const { search } = useLocation();
  const dispatch = useDispatch();

  const searchParams = queryString.parse(search);
  const token = searchParams['otk'];

  const ssoLogin = (otk) => {
    if (token === undefined) {
      navigate(AppConfig.OKTA_URL);
    }

    axios
      .get(AppConfig.FO_INITIAL_URL + '/auth/login/otk?oneTimeKey=' + otk)
      .then((res) => {
        const loginDto = res.data;
        updateDetail(loginDto);
      })
      .catch((err) => {
        navigate(AppConfig.OKTA_URL);
      });
  };

  const updateDetail = (loginDto) => {
    // Handle jsessionId
    let jsessionId = loginDto.jsessionId;
    if (!jsessionId) {
      jsessionId = generateJsessionId();
    };

    LocalStore.saveJsessionId(jsessionId);

    // --- Clarity Integration ---
    if (window.clarity) {
      var userEmail = loginDto.email?.toString().toLowerCase();
      var username = loginDto.username?.toString().toLowerCase();
      var currentTenantCd = loginDto.currentTenantCd?.toString().toLowerCase();
      var currentApp = loginDto.currentApp?.toString().toLowerCase();
      var isExternal = loginDto.isExternal;

      window.clarity('consent');
      window.clarity('identify', userEmail, jsessionId, '', '');
      window.clarity('set', 'email', userEmail);
      window.clarity('set', 'username', username);
      window.clarity('set', 'currentTenantCd', currentTenantCd);
      window.clarity('set', 'currentApp', currentApp);
      window.clarity('set', 'externalUser', isExternal);

      trackClick(trackingTypes.USER, trackingActions.LOGIN, username, 
          [userEmail, username, currentTenantCd, currentApp, isExternal].join(';'));
    }
    // --- End Clarity Integration ---
    
    LocalStore.saveLoginUser(JSON.stringify(loginDto));
    // Only save SAML login URL for external users
    if (loginDto.isExternal && loginDto.samlLoginUrl) {
      LocalStore.saveSamlLoginUrl(loginDto.samlLoginUrl);
    }
    console.log(loginDto.samlLoginUrl);

    setAxiosHeaderToken(loginDto.jwt);
    LocalStore.saveAccessToken(loginDto.jwt);

    // --- Redirection Logic ---
    const annualRenewalPath = routePaths.clientPortal + AppConfig.CLIENT_PORTAL_SUB_URL.ANNUAL_PLAN_RENEWAL;

    if (LocalStore.getReportsRedirectUrl() && isMatch(LocalStore.getReportsRedirectUrl(), REPORT_URL_REGEX_PATTERNS)) {
      // clean up local storage for wrong redirection in future
      const reportsUrl = LocalStore.getReportsRedirectUrl();
      const reportTenantCode = LocalStore.getReportTenantCode();
      dispatch({ type: LOGIN_SUCCESS, payload: { ...loginDto, currentTenantCd: reportTenantCode } });
      LocalStore.setReportsRedirectUrl(null);
      LocalStore.setReportTenantCode(null);
      setTimeout(() => {
        navigate(reportsUrl);
      }, 100);
    } else {
      dispatch({ type: LOGIN_SUCCESS, payload: loginDto });
      dispatch({ type: JWT, payload: loginDto.jwt });
      dispatch(fetchData(loginDto.currentTenantCd, loginDto.currentApp));

      // Check if the user has only one app and one permission, both being CLIENT_PORTAL
      if (loginDto.apps?.length === 1 && loginDto.permissions?.length === 1 && loginDto.apps[0] === 'CLIENT_PORTAL' && loginDto.permissions[0] === 'CLIENT_PORTAL') {
        LocalStore.setCaseRenewalRedirectUrl(null);
        setTimeout(() => {
          navigate(annualRenewalPath);
        }, 100);
      } else if (LocalStore.getCaseRenewalRedirectUrl() && LocalStore.getCaseRenewalRedirectUrl() === annualRenewalPath) {
        LocalStore.setCaseRenewalRedirectUrl(null);
        setTimeout(() => {
          navigate(annualRenewalPath);
        }, 100);
      } else {
        setTimeout(() => {
          navigate(routePaths.newHome);
        }, 100);
      }
    }
  };

  useEffect(() => {
    if (token) {
      const jwt = LocalStore.getAccessToken();
      if (!jwt || jwt === 'undefined' || Auth.getLoginMode() !== Auth.LOGIN_MODE_HST || Auth.isJwtExpired(jwt)) {
        LocalStore.clear();
        Auth.setLoginMode(Auth.LOGIN_MODE_HST);
        ssoLogin(token);
      } else {
        setTimeout(() => {
          navigate(routePaths.newHome);
        }, 100);
      }
    }
  }, [token]);

  return <>Loading...</>;
};

export default SSO;
